import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ConditionBuilderComponent } from '@/shared/condition-builder/condition-builder.component';
import { Group } from '@/shared/condition-builder/condition.model';
import { NumberRuleService } from './number-rule.service';
import { ProposalTemplateService } from '@/pages/quotation/proposal-template.service';
import { FieldLabelService } from '@/pages/service/core/field-label.service';
import { ConversationService } from '@/pages/service/core/ConversationService.service';

type ResetCadence = 'auto' | 'never' | 'daily' | 'monthly' | 'yearly';

interface RuleRow {
  templateId: string | null;
  conditions: Group;
  format: string;
  /** When the sequence restarts. 'auto' = whatever the format's date tokens imply. */
  resetCadence: ResetCadence;
  active: boolean;
}

/**
 * Settings → Numbering. Condition-based number formats for quotations and generated documents.
 *
 * <p>Rules are tried top to bottom and the first match wins, so the list is ordered and the last
 * row is expected to be an unconditional fallback. That is why the whole list saves in one go —
 * a half-saved list could leave a company with two fallbacks, or none.</p>
 */
@Component({
  selector: 'app-number-rules',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, SelectModule,
            ToastModule, TooltipModule, ConditionBuilderComponent],
  providers: [MessageService],
  template: `
<p-toast />
<div class="nr">
  <div class="nr__head">
    <div>
      <h3>Numbering</h3>
      <p class="nr__hint">
        Decide the number a quotation or document gets from the case's own data. Rules are tried
        <b>top to bottom</b> and the first match wins — so put the most specific first and leave the
        last one without conditions as the catch-all.
      </p>
    </div>
  </div>

  <div class="nr__scopes">
    <button type="button" class="nr__scope" [class.is-on]="scope === 'quotation'" (click)="setScope('quotation')">
      <i class="pi pi-file"></i> Quotations
    </button>
    <button type="button" class="nr__scope" [class.is-on]="scope === 'document'" (click)="setScope('document')">
      <i class="pi pi-file-edit"></i> Documents
    </button>
  </div>

  <div *ngIf="loading" class="nr__state"><i class="pi pi-spin pi-spinner"></i> Loading…</div>

  <ng-container *ngIf="!loading">
    <div class="nr__rule" *ngFor="let r of rules; let i = index">
      <div class="nr__rule-head">
        <span class="nr__num">{{ i + 1 }}</span>
        <span class="nr__when">{{ hasConditions(r) ? 'When…' : 'Otherwise (catch-all)' }}</span>

        <label class="nr__tpl" *ngIf="scope === 'document'">
          <span>Applies to</span>
          <p-select [options]="templateOptions" [(ngModel)]="r.templateId" [showClear]="true"
                    optionLabel="label" optionValue="value" placeholder="Every document" appendTo="body"
                    pTooltip="Leave blank to apply to every document template."
                    tooltipPosition="top" styleClass="nr__sel"></p-select>
        </label>

        <span class="nr__spacer"></span>
        <button type="button" class="nr__icon" [disabled]="i === 0" (click)="move(i, -1)" pTooltip="Move up"><i class="pi pi-arrow-up"></i></button>
        <button type="button" class="nr__icon" [disabled]="i === rules.length - 1" (click)="move(i, 1)" pTooltip="Move down"><i class="pi pi-arrow-down"></i></button>
        <button type="button" class="nr__icon nr__icon--danger" (click)="remove(i)" pTooltip="Remove"><i class="pi pi-times"></i></button>
      </div>

      <app-condition-builder [group]="r.conditions" [fields]="fields" [suggestions]="suggestions"
                             (change)="dirty = true"></app-condition-builder>

      <div class="nr__fmt">
        <label>Number format</label>
        <input pInputText [(ngModel)]="r.format" (ngModelChange)="dirty = true"
               placeholder="AGR/{{ '{YYYY}' }}/{{ '{SEQ:4}' }}" class="nr__fmtinput" />
        <label class="nr__restart">Restart
          <p-select [(ngModel)]="r.resetCadence" [options]="cadenceOptions" optionLabel="label" optionValue="value"
                    (ngModelChange)="dirty = true" appendTo="body" styleClass="nr__sel"
                    pTooltip="Auto follows the date tokens in the format. Choose a cadence to override it — a number can show the month and still keep counting."
                    tooltipPosition="top"></p-select>
        </label>
        <span class="nr__preview">
          next: <b>{{ preview(r.format) }}</b>
          <span class="nr__reset">· {{ resetLabel(r) }}</span>
        </span>
      </div>
    </div>

    <div class="nr__empty" *ngIf="!rules.length">
      No rules yet — {{ scope === 'quotation' ? 'quotations keep their current Q-00001 numbering' : 'each document template uses its own number format' }}.
    </div>

    <div class="nr__actions">
      <button type="button" class="nr__add" (click)="add()"><i class="pi pi-plus"></i> Add rule</button>
      <span class="nr__spacer"></span>
      <p-button [label]="saving ? 'Saving…' : 'Save rules'" icon="pi pi-check"
                [disabled]="saving || !dirty" (onClick)="save()" />
    </div>

    <p class="nr__tokens">
      Tokens: <code>{{ '{YYYY}' }}</code> <code>{{ '{YY}' }}</code> <code>{{ '{MM}' }}</code>
      <code>{{ '{DD}' }}</code> <code>{{ '{SEQ}' }}</code> <code>{{ '{SEQ:4}' }}</code> (zero-padded).
      Each distinct format runs its <b>own</b> sequence. By default the date tokens decide when it
      restarts — <code>{{ '{YYYY}' }}</code> starts again at 1 each year — and <b>Restart</b> overrides
      that, so a number can show the month and still count straight through.
    </p>
  </ng-container>
</div>
  `,
  styles: [`
    .nr { display:flex; flex-direction:column; gap:14px; }
    .nr__head h3 { margin:0; font-size:1.25rem; }
    .nr__hint { margin:4px 0 0; color:var(--gh-text-3); font-size:var(--gh-fs-13); max-width:62rem; line-height:1.55; }
    .nr__scopes { display:flex; gap:6px; }
    .nr__scope { display:inline-flex; align-items:center; gap:7px; padding:7px 14px; border:1px solid var(--gh-border);
                 border-radius:var(--gh-r-pill); background:var(--gh-surface); color:var(--gh-text-2);
                 font:inherit; font-size:var(--gh-fs-13); font-weight:600; cursor:pointer; }
    .nr__scope.is-on { border-color:var(--gh-primary-bright); color:var(--gh-primary);
                       background:var(--gh-primary-wash); font-weight:700; }
    .nr__state { padding:28px; text-align:center; color:var(--gh-text-3); }
    .nr__rule { border:1px solid var(--gh-border); border-radius:var(--gh-r-lg); padding:14px;
                background:var(--gh-surface); display:flex; flex-direction:column; gap:12px; }
    .nr__rule-head { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .nr__num { display:inline-flex; align-items:center; justify-content:center; width:22px; height:22px;
               border-radius:50%; background:var(--gh-primary); color:var(--gh-on-primary);
               font-size:var(--gh-fs-12); font-weight:800; }
    .nr__when { font-weight:700; color:var(--gh-text); font-size:var(--gh-fs-14); }
    .nr__tpl { display:inline-flex; align-items:center; gap:7px; font-size:var(--gh-fs-12); color:var(--gh-text-3); }
    .nr__spacer { flex:1 1 auto; }
    .nr__icon { border:1px solid var(--gh-border); background:var(--gh-surface); color:var(--gh-text-2);
                width:28px; height:28px; border-radius:var(--gh-r-sm); cursor:pointer; }
    .nr__icon[disabled] { opacity:.4; cursor:default; }
    .nr__icon--danger { color:var(--gh-danger); }
    .nr__fmt { display:flex; align-items:center; gap:10px; flex-wrap:wrap;
               border-top:1px solid var(--gh-divider); padding-top:11px; }
    .nr__fmt label { font-size:var(--gh-fs-12); font-weight:700; color:var(--gh-text-2); }
    .nr__fmtinput { min-width:16rem; }
    .nr__restart { display:inline-flex; align-items:center; gap:7px; font-size:var(--gh-fs-12);
                   font-weight:700; color:var(--gh-text-2); }
    .nr__preview { font-size:var(--gh-fs-13); color:var(--gh-text-3); }
    .nr__preview b { color:var(--gh-primary); font-variant-numeric:tabular-nums; }
    .nr__reset { color:var(--gh-text-muted); }
    .nr__empty { padding:22px; text-align:center; color:var(--gh-text-3); font-size:var(--gh-fs-13);
                 border:1px dashed var(--gh-border); border-radius:var(--gh-r-lg); }
    .nr__actions { display:flex; align-items:center; gap:10px; }
    .nr__add { display:inline-flex; align-items:center; gap:7px; border:1px dashed var(--gh-border-strong);
               background:var(--gh-surface); color:var(--gh-text-2); font:inherit; font-size:var(--gh-fs-13);
               font-weight:600; padding:8px 14px; border-radius:var(--gh-r-md); cursor:pointer; }
    .nr__tokens { margin:0; color:var(--gh-text-3); font-size:var(--gh-fs-12); line-height:1.6; }
    .nr__tokens code { background:var(--gh-surface-hover); padding:1px 5px; border-radius:4px; }
  `],
})
export class NumberRulesComponent implements OnInit {

  scope: 'quotation' | 'document' = 'quotation';
  rules: RuleRow[] = [];
  loading = false;
  saving = false;
  dirty = false;

  templateOptions: { label: string; value: string | null }[] = [];
  /** Field catalogue for the condition builder — the case's captured data keys. */
  fields: { label: string; value: string }[] = [];
  suggestions: Record<string, string[]> = {};

  constructor(
    private svc: NumberRuleService,
    private templates: ProposalTemplateService,
    private convo: ConversationService,
    private toast: MessageService,
    /** Captured-data code -> the tenant's label. Shared resolver. */
    private fieldLabels: FieldLabelService,
  ) {}

  ngOnInit(): void {
    this.fieldLabels.load().subscribe();   // the Field dropdown is entirely captured fields
    this.load();
    this.loadTemplates();
    this.loadFields();
  }

  setScope(s: 'quotation' | 'document'): void {
    if (this.scope === s) return;
    if (this.dirty && !confirm('Discard unsaved changes to these rules?')) return;
    this.scope = s;
    this.dirty = false;
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.svc.list(this.scope).subscribe({
      next: (res: any) => {
        const rows = (res?.data ?? res ?? []) as any[];
        this.rules = rows.map(r => ({
          templateId: r.templateId ?? null,
          conditions: this.asGroup(r.conditions),
          format: r.format ?? '',
          resetCadence: (r.resetCadence ?? 'auto') as ResetCadence,
          active: r.active !== false,
        }));
        this.loading = false;
      },
      error: () => { this.rules = []; this.loading = false; },
    });
  }

  private loadTemplates(): void {
    this.templates.list().subscribe({
      next: (res: any) => {
        const rows = ((res?.data ?? res ?? []) as any[]).filter(t => (t?.kind ?? 'quotation') === 'document');
        this.templateOptions = rows.map(t => ({ label: t.name ?? 'Document', value: String(t.id) }));
      },
      error: () => { this.templateOptions = []; },
    });
  }

  /** Captured-data keys, so conditions can be written against what the case actually holds. */
  private loadFields(): void {
    this.convo.getCaseDataKeys().subscribe({
      next: (res: any) => {
        const keys: string[] = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        // This component offers ONLY captured fields, so without the resolver the entire Field
    // dropdown was raw codes. Sorted by label — a human reads this list.
    this.fields = keys.filter(Boolean)
      .map(k => ({ label: this.fieldLabels.labelFor(k), value: k }))
      .sort((a, b) => a.label.localeCompare(b.label));
      },
      error: () => { this.fields = []; },
    });
  }

  private asGroup(c: any): Group {
    return (c && Array.isArray(c.rows)) ? c as Group : { join: 'AND', rows: [] } as Group;
  }

  hasConditions(r: RuleRow): boolean {
    return !!r.conditions?.rows?.length;
  }

  add(): void {
    this.rules = [...this.rules,
      { templateId: null, conditions: { join: 'AND', rows: [] } as Group, format: '', resetCadence: 'auto', active: true }];
    this.dirty = true;
  }

  remove(i: number): void {
    this.rules = this.rules.filter((_, x) => x !== i);
    this.dirty = true;
  }

  move(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.rules.length) return;
    const next = [...this.rules];
    [next[i], next[j]] = [next[j], next[i]];
    this.rules = next;
    this.dirty = true;
  }

  /** What the next number would look like — date tokens resolved, sequence shown as 1. */
  preview(format: string): string {
    if (!format || !format.trim()) return '—';
    const d = new Date();
    let out = format
      .replace(/\{YYYY\}/g, String(d.getFullYear()))
      .replace(/\{YY\}/g, String(d.getFullYear() % 100).padStart(2, '0'))
      .replace(/\{MM\}/g, String(d.getMonth() + 1).padStart(2, '0'))
      .replace(/\{DD\}/g, String(d.getDate()).padStart(2, '0'));
    out = out.replace(/\{SEQ:(\d{1,3})\}/g, (_m, w) => '1'.padStart(Math.min(+w, 12), '0'));
    return out.replace(/\{SEQ\}/g, '1');
  }

  readonly cadenceOptions = [
    { label: 'Auto (from format)', value: 'auto' },
    { label: 'Never', value: 'never' },
    { label: 'Daily', value: 'daily' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Yearly', value: 'yearly' },
  ];

  /** Say the cadence out loud — it is the one part of this that is not visible in the format box.
   *  On 'auto' the date tokens decide, so spell out what they came to rather than just "auto". */
  resetLabel(r: RuleRow): string {
    switch (r?.resetCadence) {
      case 'never':   return 'never restarts';
      case 'daily':   return 'restarts daily';
      case 'monthly': return 'restarts monthly';
      case 'yearly':  return 'restarts yearly';
      default:        return this.impliedReset(r?.format || '') + ' (from format)';
    }
  }

  private impliedReset(format: string): string {
    if (format.includes('{DD}')) return 'restarts daily';
    if (format.includes('{MM}')) return 'restarts monthly';
    if (format.includes('{YYYY}') || format.includes('{YY}')) return 'restarts yearly';
    return 'never restarts';
  }

  save(): void {
    const bad = this.rules.find(r => !r.format.trim());
    if (bad) { this.toast.add({ severity: 'warn', summary: 'Every rule needs a number format' }); return; }
    this.saving = true;
    this.svc.replace(this.scope, this.rules.map(r => ({
      templateId: r.templateId,
      conditions: this.hasConditions(r) ? r.conditions : null,
      format: r.format.trim(),
      resetCadence: r.resetCadence || 'auto',
      active: r.active,
    }))).subscribe({
      next: () => {
        this.saving = false; this.dirty = false;
        this.toast.add({ severity: 'success', summary: 'Numbering rules saved' });
      },
      error: (e: any) => {
        this.saving = false;
        this.toast.add({ severity: 'error', summary: 'Could not save', detail: e?.error?.message || 'Please try again.' });
      },
    });
  }
}
